import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Search as SearchIcon, Info as InfoIcon } from '@mui/icons-material';
import adminService from '../../services/adminService';
import { AdminLog } from '../../types';

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: 16,
  border: `1px solid ${theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.08)'}`,
  background: theme.palette.mode === 'light' 
    ? 'rgba(255, 255, 255, 0.8)'
    : 'rgba(15, 23, 42, 0.8)',
  backdropFilter: 'blur(10px)',
}));

const getActionColor = (action: string): 'error' | 'warning' | 'info' | 'success' | 'default' => {
  if (action.includes('delete') || action.includes('remove')) return 'error';
  if (action.includes('create') || action.includes('add')) return 'success';
  if (action.includes('update') || action.includes('edit')) return 'warning';
  if (action.includes('view') || action.includes('access')) return 'info';
  return 'default';
};

const ITEMS_PER_PAGE = 25;

const AdminAuditLogsPage: React.FC = () => {
  const [logs, setLogs] = useState<AdminLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [actionFilter, setActionFilter] = useState('all');
  const [page, setPage] = useState(1);

  useEffect(() => {
    const loadLogs = async () => {
      try {
        setLoading(true);
        const auditLogs = await adminService.getAuditLogs();
        // Sort by timestamp, newest first
        const sorted = auditLogs.sort((a, b) => 
          new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
        );
        setLogs(sorted);
      } catch (err) {
        console.error('Error loading audit logs:', err);
        setError('Failed to load audit logs');
      } finally {
        setLoading(false);
      }
    };

    loadLogs();
  }, []);

  const filteredLogs = logs.filter(log => {
    const matchesSearch = 
      log.action.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (log.details?.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesAction = actionFilter === 'all' || log.action === actionFilter;
    
    return matchesSearch && matchesAction;
  });

  const uniqueActions = Array.from(new Set(logs.map(log => log.action)));
  
  const paginatedLogs = filteredLogs.slice(
    (page - 1) * ITEMS_PER_PAGE,
    page * ITEMS_PER_PAGE
  );

  const totalPages = Math.ceil(filteredLogs.length / ITEMS_PER_PAGE);

  if (error) {
    return <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 700 }}>
        Audit Logs
      </Typography>

      {/* Stats */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StyledCard>
            <CardContent>
              <Typography color="textSecondary" variant="body2" gutterBottom>
                Total Events
              </Typography>
              <Typography variant="h5" sx={{ color: '#4FD1C7', fontWeight: 700 }}>
                {logs.length}
              </Typography>
            </CardContent>
          </StyledCard>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StyledCard>
            <CardContent>
              <Typography color="textSecondary" variant="body2" gutterBottom>
                Unique Actions
              </Typography>
              <Typography variant="h5" sx={{ color: '#4FD1C7', fontWeight: 700 }}>
                {uniqueActions.length}
              </Typography>
            </CardContent>
          </StyledCard>
        </Grid>
      </Grid>

      {/* Filters */}
      <StyledCard sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' }, mb: 2 }}>
            <TextField
              size="small"
              placeholder="Search by user, action, or description..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(1);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ flex: 1 }}
            />
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Action Filter</InputLabel>
              <Select
                value={actionFilter}
                label="Action Filter"
                onChange={(e) => {
                  setActionFilter(e.target.value);
                  setPage(1);
                }}
              >
                <MenuItem value="all">All Actions</MenuItem>
                {uniqueActions.map((action) => (
                  <MenuItem key={action} value={action}>
                    {action}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Typography variant="caption" color="textSecondary">
            Showing {paginatedLogs.length} of {filteredLogs.length} events
          </Typography>
        </CardContent>
      </StyledCard>

      {/* Logs Table */}
      <StyledCard>
        <CardContent>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : filteredLogs.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <InfoIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
              <Typography color="textSecondary">
                No audit logs found
              </Typography>
            </Box>
          ) : (
            <>
              <TableContainer component={Paper} sx={{ bgcolor: 'transparent', boxShadow: 'none', mb: 2 }}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'rgba(79, 209, 199, 0.1)' }}>
                      <TableCell sx={{ fontWeight: 700 }}>Timestamp</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>User ID</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Action</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Details</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedLogs.map((log) => (
                      <TableRow key={log.id} hover>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                            {new Date(log.timestamp).toLocaleString()}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                            {log.id.substring(0, 8)}...
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={log.action}
                            size="small"
                            color={getActionColor(log.action)}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="textSecondary" sx={{ maxWidth: 300 }}>
                            {log.details}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Pagination */}
              {totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={(_, value) => setPage(value)}
                    color="primary"
                  />
                </Box>
              )}
            </>
          )}
        </CardContent>
      </StyledCard>

      {/* Info */}
      <Box sx={{ mt: 3, p: 2, bgcolor: 'rgba(79, 209, 199, 0.05)', borderRadius: 2 }}>
        <Typography variant="body2" color="textSecondary">
          📋 <strong>Audit logs</strong> track all admin actions for compliance and security purposes. Logs are retained for 90 days.
        </Typography>
      </Box>
    </Box>
  );
};

export default AdminAuditLogsPage;
